import React, { useState, useEffect } from 'react';
import { glAccountsAPI } from '../api/client';
import apiClient from '../api/client';
import { X, Plus, Trash2, Building2, CheckSquare, ChevronUp, ChevronDown } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const GL_PAGE_SIZE = 50;

export default function GLAccountsSettings() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingFromFinance, setImportingFromFinance] = useState(false);
  const [deptsByGL, setDeptsByGL] = useState({});
  const [showDeptModal, setShowDeptModal] = useState(null);
  const [deptModalData, setDeptModalData] = useState({ assigned: [], available: [], loading: true });
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [showDeptAdd, setShowDeptAdd] = useState(false);
  const [selectedGLs, setSelectedGLs] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [showBulkDeptModal, setShowBulkDeptModal] = useState(false);
  const [bulkDeptAction, setBulkDeptAction] = useState('assign');
  const [bulkSelectedDepts, setBulkSelectedDepts] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [glPage, setGlPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    accountType: '',
    status: '',
    postingOnly: false
  });
  const [formData, setFormData] = useState({
    accountCode: '',
    accountName: '',
    accountType: 'Expense',
    parentAccountId: '',
    description: '',
    allowPosting: true,
    bcAccountCode: ''
  });

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense', 'CostOfSales'];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  useEffect(() => {
    fetchAccounts();
    fetchActiveIntegration();
    fetchDeptAssignments();
    fetchAllDepartments();
  }, []);

  const fetchAllDepartments = async () => {
    try {
      const res = await apiClient.get('/departments');
      const depts = res.data?.departments || res.data || [];
      setAllDepartments(depts.filter(d => d.IsActive !== false));
    } catch {
      setAllDepartments([]);
    }
  };

  const toggleGLSelection = (glAccountId) => {
    setSelectedGLs(prev =>
      prev.includes(glAccountId)
        ? prev.filter(id => id !== glAccountId)
        : [...prev, glAccountId]
    );
  };

  const toggleSelectAll = () => {
    const allIds = filteredAccounts.map(a => a.GLAccountID);
    if (selectedGLs.length === allIds.length) {
      setSelectedGLs([]);
    } else {
      setSelectedGLs(allIds);
    }
  };

  const handleBulkDeptAssign = async () => {
    if (bulkSelectedDepts.length === 0 || selectedGLs.length === 0) return;
    setBulkAssigning(true);
    try {
      const endpoint = bulkDeptAction === 'assign' ? '/department-gl-accounts/bulk-assign' : '/department-gl-accounts/bulk-unassign';
      const res = await apiClient.post(endpoint, {
        glAccountIds: selectedGLs,
        departmentIds: bulkSelectedDepts
      });
      const d = res.data;
      setShowBulkDeptModal(false);
      setBulkSelectedDepts([]);
      setSelectedGLs([]);
      fetchDeptAssignments();
      if (bulkDeptAction === 'assign') {
        alert(`Bulk assignment complete!\n\nAssigned: ${d.assigned}\nSkipped (already assigned): ${d.skipped}`);
      } else {
        alert(`Bulk unassignment complete!\n\nRemoved: ${d.removed}`);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk operation failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  const fetchDeptAssignments = async () => {
    try {
      const res = await apiClient.get('/department-gl-accounts/by-gl/all');
      setDeptsByGL(res.data?.departmentsByGL || {});
    } catch {
      setDeptsByGL({});
    }
  };

  const openDeptModal = async (account) => {
    setShowDeptModal(account);
    setShowDeptAdd(false);
    setSelectedDepts([]);
    setDeptModalData({ assigned: [], available: [], loading: true });
    try {
      const res = await apiClient.get(`/department-gl-accounts/by-gl/${account.GLAccountID}/departments`);
      setDeptModalData({ assigned: res.data.assigned || [], available: res.data.available || [], loading: false });
    } catch {
      setDeptModalData({ assigned: [], available: [], loading: false });
    }
  };

  const handleAssignDepts = async () => {
    if (selectedDepts.length === 0) return;
    try {
      await apiClient.post(`/department-gl-accounts/by-gl/${showDeptModal.GLAccountID}/departments`, { departmentIds: selectedDepts });
      setSelectedDepts([]);
      setShowDeptAdd(false);
      // Refresh
      const res = await apiClient.get(`/department-gl-accounts/by-gl/${showDeptModal.GLAccountID}/departments`);
      setDeptModalData({ assigned: res.data.assigned || [], available: res.data.available || [], loading: false });
      fetchDeptAssignments();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign departments');
    }
  };

  const handleUnassignDept = async (departmentId, deptName) => {
    if (!confirm(`Remove ${deptName} from this GL account?`)) return;
    try {
      await apiClient.delete(`/department-gl-accounts/by-gl/${showDeptModal.GLAccountID}/departments/${departmentId}`);
      const res = await apiClient.get(`/department-gl-accounts/by-gl/${showDeptModal.GLAccountID}/departments`);
      setDeptModalData({ assigned: res.data.assigned || [], available: res.data.available || [], loading: false });
      fetchDeptAssignments();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to unassign department');
    }
  };

  const fetchActiveIntegration = async () => {
    try {
      const res = await apiClient.get('/finance-integrations');
      setActiveIntegration(res.data?.activeIntegration || null);
    } catch {
      setActiveIntegration(null);
    }
  };

  const handleImportFromFinance = async () => {
    setImportingFromFinance(true);
    try {
      const res = await glAccountsAPI.importFromIntegration();
      const d = res.data;
      setShowImportModal(false);
      alert(`Import completed!\n\nNew: ${d.imported}\nUpdated: ${d.updated}\nSkipped (unchanged): ${d.skipped}\nTotal fetched: ${d.totalFetched}${d.errors ? '\n\nErrors:\n' + d.errors.join('\n') : ''}`);
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to import from finance system');
    } finally {
      setImportingFromFinance(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await glAccountsAPI.getAll();
      const accountsList = Array.isArray(response) ? response : (response.data?.glAccounts || response.data || []);
      setAccounts(accountsList);
    } catch (error) {
      console.error('Fetch GL accounts error:', error);
      alert('Failed to load GL accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAccount(null);
    setFormData({
      accountCode: '',
      accountName: '',
      accountType: 'Expense',
      parentAccountId: '',
      description: '',
      allowPosting: true,
      bcAccountCode: ''
    });
    setShowModal(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      accountCode: account.AccountCode,
      accountName: account.AccountName,
      accountType: account.AccountType,
      parentAccountId: account.ParentAccountID || '',
      description: account.Description || '',
      allowPosting: account.AllowPosting,
      bcAccountCode: account.BCAccountCode || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAccount) {
        await glAccountsAPI.update(editingAccount.GLAccountID, {
          ...formData,
          isActive: true
        });
        alert('GL account updated successfully');
      } else {
        await glAccountsAPI.create(formData);
        alert('GL account created successfully');
      }

      setShowModal(false);
      fetchAccounts();
    } catch (error) {
      console.error('Save GL account error:', error);
      alert(error.response?.data?.message || 'Failed to save GL account');
    }
  };

  const handleDeactivate = async (accountId) => {
    if (!confirm('Deactivate this GL account?')) return;

    try {
      await glAccountsAPI.delete(accountId);
      alert('GL account deactivated successfully');
      fetchAccounts();
    } catch (error) {
      console.error('Deactivate GL account error:', error);
      alert(error.response?.data?.message || 'Failed to deactivate GL account');
    }
  };

  const handleImportFile = async (e) => {
    e.preventDefault();

    if (!importFile) {
      alert('Please select a file to import');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);

      const response = await glAccountsAPI.importExcel(formDataUpload);
      const result = response.data;
      
      alert(`Import completed!\nImported: ${result.imported}\nSkipped: ${result.skipped}${result.errors ? '\n\nErrors:\n' + result.errors.join('\n') : ''}`);
      
      setImportFile(null);
      fetchAccounts();
    } catch (error) {
      console.error('Import error:', error);
      alert(error.response?.data?.message || 'Failed to import GL accounts');
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Account Code', 'Account Name', 'Account Type', 'Parent Code', 'Description', 'Allow Posting'],
      ['1000', 'Cash and Bank', 'Asset', '', 'Cash and bank accounts', 'No'],
      ['1100', 'Petty Cash', 'Asset', '1000', 'Petty cash account', 'Yes'],
      ['2000', 'Accounts Payable', 'Liability', '', 'Vendor payables', 'Yes'],
      ['5000', 'Operating Expenses', 'Expense', '', 'General operating expenses', 'No'],
      ['5100', 'Office Supplies', 'Expense', '5000', 'Office supplies expense', 'Yes']
    ];

    const csvContent = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gl-accounts-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.AccountType;
    if (!groups[type]) groups[type] = [];
    groups[type].push(account);
    return groups;
  }, {});

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    // Search filter (code or name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        account.AccountCode.toLowerCase().includes(searchLower) ||
        account.AccountName.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Account type filter
    if (filters.accountType && account.AccountType !== filters.accountType) {
      return false;
    }

    // Status filter
    if (filters.status === 'active' && !account.IsActive) return false;
    if (filters.status === 'inactive' && account.IsActive) return false;

    // Posting only filter
    if (filters.postingOnly && !account.AllowPosting) return false;

    return true;
  });

  // Sort filtered accounts
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'AccountCode':
      case 'AccountName':
      case 'AccountType':
      case 'BCAccountCode':
      case 'ParentAccountCode':
        return dir * (a[sortField] || '').localeCompare(b[sortField] || '');
      case 'AllowPosting':
      case 'IsActive':
        return dir * ((a[sortField] === b[sortField]) ? 0 : a[sortField] ? -1 : 1);
      case 'Departments': {
        const aCount = deptsByGL[a.GLAccountID]?.count || 0;
        const bCount = deptsByGL[b.GLAccountID]?.count || 0;
        return dir * (aCount - bCount);
      }
      default:
        return 0;
    }
  });

  // Group filtered accounts
  const pagedAccounts = sortedAccounts.slice((glPage - 1) * GL_PAGE_SIZE, glPage * GL_PAGE_SIZE);
  const filteredGroupedAccounts = pagedAccounts.reduce((groups, account) => {
    const type = account.AccountType;
    if (!groups[type]) groups[type] = [];
    groups[type].push(account);
    return groups;
  }, {});

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">GL Accounts (Chart of Accounts)</h2>
          <p className="text-sm text-gray-600 mt-1">Manage your general ledger accounts</p>
        </div>
        <div className="flex gap-3">
          {activeIntegration && (
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center gap-2"
            >
              🔄 Import from {activeIntegration.DisplayName || 'Finance System'}
            </button>
          )}
          <button
            onClick={downloadTemplate}
            className="btn btn-secondary"
          >
            📥 Download Template
          </button>
          <button
            onClick={handleAdd}
            className="btn btn-primary"
          >
            + Add GL Account
          </button>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Import from Excel</h3>
        <form onSubmit={handleImportFile} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
          </div>
          <button
            type="submit"
            className="btn btn-success"
            disabled={!importFile}
          >
            📤 Import
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-2">
          Supported formats: Excel (.xlsx, .xls) or CSV. Download the template for correct format.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder="Code or name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type
            </label>
            <select
              value={filters.accountType}
              onChange={(e) => setFilters({ ...filters, accountType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              {accountTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              &nbsp;
            </label>
            <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={filters.postingOnly}
                onChange={(e) => setFilters({ ...filters, postingOnly: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Posting Only</span>
            </label>
          </div>
        </div>

        {/* Clear Filters */}
        {(filters.search || filters.accountType || filters.status || filters.postingOnly) && (
          <div className="mt-3 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Showing {filteredAccounts.length} of {accounts.length} accounts
            </span>
            <button
              onClick={() => setFilters({ search: '', accountType: '', status: '', postingOnly: false })}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {selectedGLs.length > 0 && (
        <div className="bg-blue-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium">{selectedGLs.length} GL account{selectedGLs.length > 1 ? 's' : ''} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setBulkDeptAction('assign'); setBulkSelectedDepts([]); setShowBulkDeptModal(true); }}
              className="px-3 py-1.5 bg-white text-blue-700 rounded-md text-sm font-medium hover:bg-blue-50 flex items-center gap-1.5"
            >
              <Building2 className="w-4 h-4" />
              Assign to Departments
            </button>
            <button
              onClick={() => { setBulkDeptAction('unassign'); setBulkSelectedDepts([]); setShowBulkDeptModal(true); }}
              className="px-3 py-1.5 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" />
              Remove from Departments
            </button>
            <button
              onClick={() => setSelectedGLs([])}
              className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-400"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Accounts List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-center w-10">
                  <input
                    type="checkbox"
                    checked={filteredAccounts.length > 0 && selectedGLs.length === filteredAccounts.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AccountCode')}><div className="flex items-center gap-1">Code<SortIcon field="AccountCode" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AccountName')}><div className="flex items-center gap-1">Account Name<SortIcon field="AccountName" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AccountType')}><div className="flex items-center gap-1">Type<SortIcon field="AccountType" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BCAccountCode')}><div className="flex items-center gap-1">BC GL Code<SortIcon field="BCAccountCode" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ParentAccountCode')}><div className="flex items-center gap-1">Parent<SortIcon field="ParentAccountCode" /></div></th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AllowPosting')}><div className="flex items-center justify-center gap-1">Posting<SortIcon field="AllowPosting" /></div></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Departments')}><div className="flex items-center gap-1">Departments<SortIcon field="Departments" /></div></th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('IsActive')}><div className="flex items-center justify-center gap-1">Status<SortIcon field="IsActive" /></div></th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(filteredGroupedAccounts).length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    No accounts found matching your filters
                  </td>
                </tr>
              ) : (
                Object.entries(filteredGroupedAccounts).map(([type, typeAccounts]) => (
                  <React.Fragment key={type}>
                    <tr className="bg-gray-100">
                      <td colSpan="10" className="px-4 py-2 text-sm font-semibold text-gray-700">
                        {type}
                      </td>
                    </tr>
                    {typeAccounts.map(account => (
                      <tr key={account.GLAccountID} className={`hover:bg-gray-50 ${selectedGLs.includes(account.GLAccountID) ? 'bg-blue-50' : ''}`}>
                        <td className="px-3 py-3 text-center w-10">
                          <input
                            type="checkbox"
                            checked={selectedGLs.includes(account.GLAccountID)}
                            onChange={() => toggleGLSelection(account.GLAccountID)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {account.AccountCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {account.AccountName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {account.AccountType}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {account.BCAccountCode ? (
                            <span className="text-xs font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">{account.BCAccountCode}</span>
                          ) : (
                            <span className="text-xs text-amber-500">Not mapped</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {account.ParentAccountCode || '-'}
                        </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {account.AllowPosting ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-gray-400">Header</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => openDeptModal(account)}
                          className="inline-flex items-center gap-1.5 text-sm hover:bg-gray-100 rounded px-2 py-1 transition-colors"
                          title="Manage department assignments"
                        >
                          <Building2 className="w-3.5 h-3.5 text-gray-500" />
                          {deptsByGL[account.GLAccountID] ? (
                            <span className="flex flex-wrap gap-1">
                              {deptsByGL[account.GLAccountID].departments.slice(0, 3).map(d => (
                                <span key={d.DepartmentID} className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  {d.DepartmentName}
                                </span>
                              ))}
                              {deptsByGL[account.GLAccountID].count > 3 && (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                  +{deptsByGL[account.GLAccountID].count - 3}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {account.IsActive ? (
                          <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        <button
                          onClick={() => handleEdit(account)}
                          className="text-blue-600 hover:text-blue-700 px-2 py-1"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeactivate(account.GLAccountID)}
                          className="text-red-600 hover:text-red-700 px-2 py-1 ml-2"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
              )}
            </tbody>
          </table>
          <TablePagination page={glPage} pageSize={GL_PAGE_SIZE} total={sortedAccounts.length} onPageChange={setGlPage} />

          {accounts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No GL accounts found. Click "Add GL Account" or import from Excel to get started.
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingAccount ? 'Edit GL Account' : 'Add GL Account'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.accountCode}
                    onChange={(e) => setFormData({ ...formData, accountCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {accountTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Account (Optional)
                </label>
                <select
                  value={formData.parentAccountId}
                  onChange={(e) => setFormData({ ...formData, parentAccountId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None (Top Level)</option>
                  {accounts
                    .filter(a => a.GLAccountID !== editingAccount?.GLAccountID)
                    .map(account => (
                      <option key={account.GLAccountID} value={account.GLAccountID}>
                        {account.AccountCode} - {account.AccountName}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowPosting"
                  checked={formData.allowPosting}
                  onChange={(e) => setFormData({ ...formData, allowPosting: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allowPosting" className="ml-2 block text-sm text-gray-700">
                  Allow Posting (uncheck for header/grouping accounts only)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BC GL Account No. <span className="text-xs text-gray-400">(Finance System Mapping)</span>
                </label>
                <input
                  type="text"
                  value={formData.bcAccountCode}
                  onChange={(e) => setFormData({ ...formData, bcAccountCode: e.target.value })}
                  placeholder="e.g. 8200 — corresponding account in Business Central"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Map this SpocoDoc GL account to a Business Central GL account number. Used when posting invoices to BC.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import from Finance System Modal */}
      {showImportModal && activeIntegration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Import GL Accounts from {activeIntegration.DisplayName || 'Finance System'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This will fetch all GL accounts (Chart of Accounts) from your connected finance system and import them into SpocoDoc.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-800">
              <p className="font-medium mb-1">What will happen:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>New accounts</strong> will be created</li>
                <li><strong>Existing accounts</strong> (matching code) will be updated if name/type changed</li>
                <li><strong>Unchanged accounts</strong> will be skipped</li>
                <li>Blocked/deleted accounts in the finance system will be excluded</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="btn btn-secondary"
                disabled={importingFromFinance}
              >
                Cancel
              </button>
              <button
                onClick={handleImportFromFinance}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
                disabled={importingFromFinance}
              >
                {importingFromFinance ? '⏳ Importing...' : '🔄 Start Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department Assignments Modal */}
      {showDeptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Department Assignments</h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {showDeptModal.AccountCode} — {showDeptModal.AccountName}
                </p>
              </div>
              <button onClick={() => setShowDeptModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {deptModalData.loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (
                <>
                  {/* Assigned Departments */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Assigned Departments ({deptModalData.assigned.length})
                      </h4>
                      {deptModalData.available.length > 0 && (
                        <button
                          onClick={() => { setShowDeptAdd(!showDeptAdd); setSelectedDepts([]); }}
                          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add Departments
                        </button>
                      )}
                    </div>

                    {deptModalData.assigned.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        No departments assigned. Click "Add Departments" to assign.
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {deptModalData.assigned.map(d => (
                              <tr key={d.DepartmentID} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                  {d.DepartmentName}
                                  {d.DepartmentCode && <span className="text-gray-400 ml-1">({d.DepartmentCode})</span>}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">
                                  {d.AssignedAt ? new Date(d.AssignedAt).toLocaleDateString() : '—'}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <button
                                    onClick={() => handleUnassignDept(d.DepartmentID, d.DepartmentName)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Remove"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Add Departments Section (collapsible) */}
                  {showDeptAdd && deptModalData.available.length > 0 && (
                    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Select departments to assign:
                      </h4>
                      <div className="max-h-48 overflow-y-auto space-y-1 mb-3">
                        {deptModalData.available.map(d => (
                          <label key={d.DepartmentID} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-100 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedDepts.includes(d.DepartmentID)}
                              onChange={() => {
                                setSelectedDepts(prev =>
                                  prev.includes(d.DepartmentID)
                                    ? prev.filter(id => id !== d.DepartmentID)
                                    : [...prev, d.DepartmentID]
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-900">{d.DepartmentName}</span>
                            {d.DepartmentCode && <span className="text-xs text-gray-400">({d.DepartmentCode})</span>}
                          </label>
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">{selectedDepts.length} selected</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setShowDeptAdd(false); setSelectedDepts([]); }}
                            className="text-sm px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAssignDepts}
                            disabled={selectedDepts.length === 0}
                            className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            Assign ({selectedDepts.length})
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end bg-gray-50">
              <button
                onClick={() => setShowDeptModal(null)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Department Assignment Modal */}
      {showBulkDeptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">
                {bulkDeptAction === 'assign' ? 'Assign to Departments' : 'Remove from Departments'}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                {selectedGLs.length} GL account{selectedGLs.length > 1 ? 's' : ''} selected
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-gray-700 mb-3">
                {bulkDeptAction === 'assign'
                  ? 'Select the departments to assign the selected GL accounts to:'
                  : 'Select the departments to remove the selected GL accounts from:'}
              </p>

              {allDepartments.length === 0 ? (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg text-sm">
                  No departments found. Create departments first.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => setBulkSelectedDepts(allDepartments.map(d => d.DepartmentID))}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setBulkSelectedDepts([])}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-2">
                    {allDepartments.map(d => (
                      <label key={d.DepartmentID} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkSelectedDepts.includes(d.DepartmentID)}
                          onChange={() => {
                            setBulkSelectedDepts(prev =>
                              prev.includes(d.DepartmentID)
                                ? prev.filter(id => id !== d.DepartmentID)
                                : [...prev, d.DepartmentID]
                            );
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900 font-medium">{d.DepartmentName}</span>
                        {d.DepartmentCode && <span className="text-xs text-gray-400">({d.DepartmentCode})</span>}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center bg-gray-50">
              <span className="text-xs text-gray-600">
                {bulkSelectedDepts.length} department{bulkSelectedDepts.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowBulkDeptModal(false); setBulkSelectedDepts([]); }}
                  className="btn btn-secondary"
                  disabled={bulkAssigning}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeptAssign}
                  disabled={bulkSelectedDepts.length === 0 || bulkAssigning}
                  className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${
                    bulkDeptAction === 'assign'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {bulkAssigning
                    ? '⏳ Processing...'
                    : bulkDeptAction === 'assign'
                      ? `Assign to ${bulkSelectedDepts.length} Department${bulkSelectedDepts.length !== 1 ? 's' : ''}`
                      : `Remove from ${bulkSelectedDepts.length} Department${bulkSelectedDepts.length !== 1 ? 's' : ''}`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
